import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { VenteForm } from '../components/ventes/VenteForm';
import { formatDate, formatEuro } from '../lib/format';
import { StatutPaiementBadge } from '../components/ui/Badge';
import { EcheancierTab } from '../components/tresorerie/EcheancierTab';
import { AvoirsTab } from '../components/tresorerie/AvoirsTab';

export function VenteDetailPage(): JSX.Element {
  const { id } = useParams();
  const ventId = id !== undefined ? Number.parseInt(id, 10) : NaN;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<'apercu' | 'echeancier' | 'avoirs'>('apercu');

  const query = useQuery({
    queryKey: ['ventes', 'detail', ventId],
    queryFn: () => api.ventes.get(ventId),
    enabled: !Number.isNaN(ventId),
  });

  const contactQuery = useQuery({
    queryKey: ['contacts', 'detail', query.data?.vente.contact_id ?? null],
    queryFn: () =>
      query.data !== undefined
        ? api.contacts.get(query.data.vente.contact_id)
        : Promise.resolve(null),
    enabled: query.data !== undefined,
  });

  const update = useMutation({
    mutationFn: (body: Parameters<typeof api.ventes.update>[1]) => api.ventes.update(ventId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['ventes'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      setEditing(false);
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : 'Erreur mise à jour');
    },
  });

  const remove = useMutation({
    mutationFn: () => api.ventes.delete(ventId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['ventes'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      navigate('/ventes');
    },
  });

  if (Number.isNaN(ventId)) return <div className="text-sm text-red-400">ID invalide.</div>;
  if (query.isLoading) return <div className="text-sm text-slate-400">Chargement…</div>;
  if (query.isError || query.data === undefined)
    return (
      <div className="text-sm text-red-400">
        Vente introuvable.{' '}
        <Link to="/ventes" className="underline">
          Retour à la liste
        </Link>
      </div>
    );

  const { vente, lignes } = query.data;

  if (editing) {
    return (
      <div className="max-w-5xl">
        <header className="mb-6">
          <h1 className="text-2xl font-bold text-slate-100">Modifier la vente #{vente.id}</h1>
        </header>
        {error !== null && (
          <div className="mb-4 text-xs text-red-300 bg-red-900/20 border border-red-900/50 rounded-md px-3 py-2">
            {error}
          </div>
        )}
        <VenteForm
          initial={{
            vente,
            lignes,
            contact: null, // l'autocomplete est désactivé en edit (contact_id figé)
          }}
          submitting={update.isPending}
          submitLabel="Enregistrer"
          onSubmit={(payload): void => update.mutate(payload)}
          onCancel={(): void => setEditing(false)}
        />
      </div>
    );
  }

  return (
    <div className="max-w-5xl">
      <header className="flex items-end justify-between mb-6">
        <div>
          <div className="text-xs text-slate-500 mb-1">Vente #{vente.id}</div>
          <h1 className="text-2xl font-bold text-slate-100">
            {vente.numero_facture_henrri ?? 'Sans n° facture'} — {formatDate(vente.date_vente)}
          </h1>
          {contactQuery.data !== undefined && contactQuery.data !== null && (
            <p className="text-sm text-slate-400 mt-1">
              Client :{' '}
              <Link
                to={`/contacts/${String(contactQuery.data.contact.id)}`}
                className="text-floryon-green hover:underline"
              >
                {contactQuery.data.contact.societe ??
                  `${contactQuery.data.contact.prenom ?? ''} ${contactQuery.data.contact.nom}`.trim()}
              </Link>
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={(): void => setEditing(true)}>
            Modifier
          </Button>
          <Button variant="danger" onClick={(): void => setDeleteOpen(true)}>
            Supprimer
          </Button>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="bg-slate-900/40 border border-slate-800 rounded-xl p-4">
          <div className="text-xs text-slate-500">Total HT</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">
            {formatEuro(vente.total_ht_centimes)}
          </div>
        </div>
        <div className="bg-slate-900/40 border border-slate-800 rounded-xl p-4">
          <div className="text-xs text-slate-500">Total TTC</div>
          <div className="text-2xl font-bold text-floryon-green mt-1">
            {formatEuro(vente.total_ttc_centimes)}
          </div>
        </div>
        <div className="bg-slate-900/40 border border-slate-800 rounded-xl p-4">
          <div className="text-xs text-slate-500">Marge brute</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">
            {formatEuro(vente.marge_brute_centimes)}
          </div>
        </div>
      </div>

      <div className="flex gap-2 border-b border-slate-800 mb-4">
        <TabButton active={tab === 'apercu'} onClick={(): void => setTab('apercu')}>
          Aperçu
        </TabButton>
        <TabButton active={tab === 'echeancier'} onClick={(): void => setTab('echeancier')}>
          Échéancier
        </TabButton>
        <TabButton active={tab === 'avoirs'} onClick={(): void => setTab('avoirs')}>
          Avoirs
        </TabButton>
      </div>

      {tab === 'echeancier' && (
        <div className="bg-slate-900/40 border border-slate-800 rounded-xl p-5 mb-6">
          <EcheancierTab venteId={vente.id} />
        </div>
      )}

      {tab === 'avoirs' && (
        <div className="bg-slate-900/40 border border-slate-800 rounded-xl p-5 mb-6">
          <AvoirsTab venteId={vente.id} />
        </div>
      )}

      {tab === 'apercu' && (
        <>
          <div className="bg-slate-900/40 border border-slate-800 rounded-xl p-5 mb-6">
            <h2 className="text-sm font-semibold text-slate-200 mb-3">Détails</h2>
            <dl className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2 text-sm">
              <Field label="Statut paiement">
                <StatutPaiementBadge statut={vente.statut_paiement} />
              </Field>
              <Field label="Mode paiement" value={vente.mode_paiement} />
              <Field label="Date émission" value={formatDate(vente.date_emission)} />
              <Field label="Date échéance" value={formatDate(vente.date_echeance)} />
              <Field label="Date encaissement" value={formatDate(vente.date_encaissement)} />
              <Field label="Remise globale" value={`${String(vente.remise_globale_pct / 100)} %`} />
            </dl>
            {vente.notes !== null && vente.notes !== '' && (
              <div className="mt-4 pt-4 border-t border-slate-800">
                <div className="text-xs text-slate-500 mb-1">Notes</div>
                <div className="text-sm text-slate-200 whitespace-pre-wrap">{vente.notes}</div>
              </div>
            )}
          </div>

          <div className="bg-slate-900/40 border border-slate-800 rounded-xl overflow-hidden">
            <h2 className="text-sm font-semibold text-slate-200 px-5 py-3 border-b border-slate-800">
              Lignes ({lignes.length})
            </h2>
            <table className="w-full text-sm">
              <thead className="bg-slate-900 text-xs text-slate-400 uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-2 text-left">Désignation</th>
                  <th className="px-4 py-2 text-right">m²</th>
                  <th className="px-4 py-2 text-right">Prix € HT/m²</th>
                  <th className="px-4 py-2 text-right">Remise</th>
                  <th className="px-4 py-2 text-right">HT</th>
                  <th className="px-4 py-2 text-right">TVA</th>
                  <th className="px-4 py-2 text-right">TTC</th>
                  <th className="px-4 py-2 text-right">Marge</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {lignes.map((l) => (
                  <tr key={l.id}>
                    <td className="px-4 py-2 text-slate-200">{l.reference_libre ?? '—'}</td>
                    <td className="px-4 py-2 text-right text-slate-200">
                      {(l.m2_centi / 100).toFixed(2)}
                    </td>
                    <td className="px-4 py-2 text-right text-slate-200">
                      {formatEuro(l.prix_unitaire_centimes_par_m2)}
                    </td>
                    <td className="px-4 py-2 text-right text-slate-400">
                      {(l.remise_ligne_pct / 100).toFixed(2)} %
                    </td>
                    <td className="px-4 py-2 text-right text-slate-200">
                      {formatEuro(l.montant_ht_centimes)}
                    </td>
                    <td className="px-4 py-2 text-right text-slate-400">
                      {formatEuro(l.montant_tva_centimes)}
                    </td>
                    <td className="px-4 py-2 text-right text-slate-100 font-medium">
                      {formatEuro(l.montant_ttc_centimes)}
                    </td>
                    <td className="px-4 py-2 text-right text-slate-200">
                      {formatEuro(l.marge_ht_centimes)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <Modal
        isOpen={deleteOpen}
        onClose={(): void => setDeleteOpen(false)}
        title="Supprimer cette vente ?"
        footer={
          <>
            <Button variant="secondary" type="button" onClick={(): void => setDeleteOpen(false)}>
              Annuler
            </Button>
            <Button
              variant="danger"
              type="button"
              onClick={(): void => remove.mutate()}
              disabled={remove.isPending}
            >
              {remove.isPending ? 'Suppression…' : 'Supprimer'}
            </Button>
          </>
        }
      >
        <p className="text-sm text-slate-300">
          La vente du {formatDate(vente.date_vente)} (TTC {formatEuro(vente.total_ttc_centimes)})
          sera marquée comme supprimée. Cette action peut être annulée manuellement (la donnée reste
          en base).
        </p>
      </Modal>
    </div>
  );
}

function Field({
  label,
  value,
  children,
}: {
  label: string;
  value?: string | null;
  children?: React.ReactNode;
}): JSX.Element {
  return (
    <div>
      <dt className="text-xs text-slate-500">{label}</dt>
      <dd className="text-sm text-slate-200">{children ?? value ?? '—'}</dd>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
        active
          ? 'text-floryon-green border-floryon-green'
          : 'text-slate-400 border-transparent hover:text-slate-200'
      }`}
    >
      {children}
    </button>
  );
}
